import {
  getModuleConfig,
  type ModuleIntroConfig
} from "@/components/onboarding/configs/getModuleConfig";
import { MESSAGE } from "@/constants/message";
import type { ProductOverviewState } from "@/hooks/onboarding/useProductOverview";

import { OnboardingChecklistItem } from "./OnboardingChecklistItem";

type OnboardingChecklistProps = Pick<
  ProductOverviewState,
  "modules" | "progressByModuleId" | "isLoading" | "isError"
> & {
  onStart: (code: string, intro?: ModuleIntroConfig) => void;
};

const ChecklistSkeleton = () => (
  <div className="flex flex-col gap-3" aria-hidden>
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex items-center gap-3 p-2">
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-neutral-200" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-3 w-1/3 animate-pulse rounded bg-neutral-200" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-neutral-200" />
        </div>
      </div>
    ))}
  </div>
);

export const OnboardingChecklist = ({
  modules,
  progressByModuleId,
  isLoading,
  isError,
  onStart
}: OnboardingChecklistProps) => {
  if (isLoading) return <ChecklistSkeleton />;

  if (isError)
    return (
      <p className="px-2 text-sm text-red-500">
        {MESSAGE.PRODUCT_OVERVIEW.LOAD_FAILED}
      </p>
    );

  if (modules.length === 0)
    return (
      <p className="px-2 text-sm text-dim-gray">
        {MESSAGE.PRODUCT_OVERVIEW.EMPTY}
      </p>
    );

  return (
    <div className="flex flex-col">
      {modules.map((module, idx) => {
        const moduleConfig = getModuleConfig(module.code);

        return (
          <OnboardingChecklistItem
            key={module.id}
            module={module}
            intro={moduleConfig?.intro}
            totalSteps={moduleConfig?.steps.length ?? 0}
            index={idx + 1}
            progress={progressByModuleId[module.id]}
            onStart={onStart}
          />
        );
      })}
    </div>
  );
};

export default OnboardingChecklist;
